import type {
  AskUserQuestionItem,
  CompletedOperation,
  TableItem,
} from "@redotech/redo-merchant-inventory-app/intelligent-inventory/hooks/use-intelligent-inventory";
import * as z from "zod";

const InventoryTableSchema = z.object({
  title: z.string().optional(),
  headers: z.array(z.string()),
  rows: z.array(z.array(z.string())),
  toolCallId: z.string().optional(),
});

const OperationTypeSchema = z.enum([
  "create",
  "update",
  "delete",
  "link_suppliers",
  "create_supplier",
  "update_supplier",
  "create_bom",
  "update_bom",
  "delete_bom",
  "create_location",
  "set_product_locations",
  "create_purchase_order",
  "update_purchase_order",
  "adjust_inventory",
  "adjust_forecasts",
  "create_transfer",
  "submit_transfer",
]);

const InventoryCompletedOperationSchema = z.object({
  type: OperationTypeSchema,
  productCount: z.number().optional(),
  skuCount: z.number().optional(),
  supplierCount: z.number().optional(),
  bomCount: z.number().optional(),
  locationCount: z.number().optional(),
  purchaseOrderCount: z.number().optional(),
  transferCount: z.number().optional(),
  forecastAdjustmentCount: z.number().optional(),
  linksUpdated: z.number().optional(),
  fields: z.array(z.string()).optional(),
  forecastingEnabled: z.boolean().optional(),
  toolCallId: z.string().optional(),
  adjustmentSessionKind: z.string().optional(),
  failed: z.boolean().optional(),
});

const UnsupportedChartSchema = z.object({
  title: z.string().optional(),
  chartType: z.string().optional(),
  message: z.string(),
  toolCallId: z.string().optional(),
});

export type UnsupportedInventoryChart = z.infer<typeof UnsupportedChartSchema>;

const AnswerOptionSchema = z.object({
  optionTitle: z.string(),
  optionDescription: z.string().nullish(),
  recommended: z.boolean().nullish(),
});

export const AskUserQuestionInputSchema = z.object({
  questionType: z.enum(["singleSelect", "multiSelect"]),
  question: z.string(),
  questionContext: z.string().nullish(),
  answerOptions: z.array(AnswerOptionSchema),
});

const AskUserQuestionOutputSchema = z.object({
  toolCallId: z.string().optional(),
  input: AskUserQuestionInputSchema,
});

const InventoryCodeModeOutputSchema = z.object({
  inventoryTables: z.array(InventoryTableSchema).optional(),
  inventoryChartMessages: z.array(UnsupportedChartSchema).optional(),
  completedOperations: z.array(InventoryCompletedOperationSchema).optional(),
  askUserQuestion: AskUserQuestionOutputSchema.optional(),
});

export interface InventoryCodeModeCardsData {
  tables: TableItem[];
  chartMessages: UnsupportedInventoryChart[];
  operations: CompletedOperation[];
  questions: AskUserQuestionItem[];
}

export function tryParseInventoryCodeModeOutput(
  output: unknown,
): InventoryCodeModeCardsData | null {
  const parsed = InventoryCodeModeOutputSchema.safeParse(output);
  if (!parsed.success) {
    return null;
  }

  const tables: TableItem[] =
    parsed.data.inventoryTables?.map((table) => ({
      type: "table",
      ...table,
    })) ?? [];
  const operations: CompletedOperation[] =
    parsed.data.completedOperations?.map((operation) => ({
      ...operation,
      type: "completed-operation",
      operationType: operation.type,
    })) ?? [];
  const questions: AskUserQuestionItem[] = parsed.data.askUserQuestion
    ? [
        {
          type: "ask-user-question",
          toolCallId: parsed.data.askUserQuestion.toolCallId ?? "",
          input: parsed.data.askUserQuestion.input,
        },
      ]
    : [];
  const chartMessages = parsed.data.inventoryChartMessages ?? [];

  if (
    tables.length === 0 &&
    operations.length === 0 &&
    questions.length === 0 &&
    chartMessages.length === 0
  ) {
    return null;
  }

  return { tables, chartMessages, operations, questions };
}

export function formatAskUserQuestionResponse(result: unknown): string {
  const parsed = z
    .object({
      selectedOptions: z.array(z.string()).optional(),
      otherText: z.string().optional(),
    })
    .safeParse(result);

  if (!parsed.success) {
    return String(result);
  }

  const parts = [
    ...(parsed.data.selectedOptions ?? []),
    parsed.data.otherText?.trim(),
  ].filter((part): part is string => !!part && part.trim().length > 0);

  return parts.length > 0 ? parts.join(", ") : "No selection";
}
